import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import "./index.css";

/* Actions */
import { setAuth, setUserData } from "../../../../redux/actions/user";
import { setLoginEmail, setLoginError, clearLogins } from "../../../../redux/actions/login";

/* Components */
import TextInputWithIcon from "../../../../components/TextInputWithIcon";
import Button from "../../../../components/Button";

/* API requests */
import { lookupEmail, registerPasswordless, verifyMagicLink } from "../../../../api-requests/user";

const STEPS = {
    EMAIL: "email",
    NAME: "name",
    CODE: "code",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PAGE_TITLES = {
    [STEPS.EMAIL]: "Sign In | J4Crew",
    [STEPS.NAME]: "Create Account | J4Crew",
    [STEPS.CODE]: "Enter Code | J4Crew",
};

class Login extends Component {

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            step: STEPS.EMAIL,
            isNewUser: false,
            fullName: "",
            magicCode: "",
        };

    }

    componentDidMount() {
        document.title = PAGE_TITLES[this.state.step];
    }

    componentDidUpdate(_, prevState) {
        if (prevState.step !== this.state.step) {
            document.title = PAGE_TITLES[this.state.step];
        }
    }

    componentWillUnmount() {
        document.title = "J4Crew";
    }

    handleContinueWithEmail = (rawEmail) => {

        const email = (rawEmail || "").trim();

        if (!email) {
            return this.props.setLoginError(true, "Please enter your email address.");
        }

        if (!EMAIL_REGEX.test(email)) {
            return this.props.setLoginError(true, "That doesn't look like a valid email address.");
        }

        return this.setState({ loading: true }, () => {
            return lookupEmail(email)
                .then(result => {
                    if (result.exists === true) {
                        return this.setState({
                            loading: false,
                            step: STEPS.CODE,
                            isNewUser: false,
                            magicCode: "",
                        }, () => this.props.setLoginError(false, ""));
                    }
                    if (result.exists === false) {
                        return this.setState({
                            loading: false,
                            step: STEPS.NAME,
                            isNewUser: true,
                        }, () => this.props.setLoginError(false, ""));
                    }
                    return this.setState({ loading: false }, () => {
                        this.props.setLoginError(true, "Something went wrong. Please try again.");
                    });
                });
        });

    }

    handleCreateAccount = (email, fullName) => {

        const trimmed = (fullName || "").trim();
        if (!trimmed) {
            return this.props.setLoginError(true, "Please enter your name.");
        }

        const parts = trimmed.split(/\s+/);
        const firstName = parts[0];
        const lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";

        return this.setState({ loading: true }, () => {
            return registerPasswordless(firstName, lastName, email)
                .then(result => {
                    if (result.error_code === "already-exists") {
                        // Race: someone (maybe the same user in another tab) registered
                        // this email between the lookup and the register. Fall through
                        // to the existing-user code flow.
                        return lookupEmail(email).then(() => {
                            return this.setState({
                                loading: false,
                                step: STEPS.CODE,
                                isNewUser: false,
                                magicCode: "",
                            }, () => this.props.setLoginError(false, ""));
                        });
                    }
                    if (result.error) {
                        return this.setState({ loading: false }, () => {
                            this.props.setLoginError(true, result.error_message || "Could not create your account.");
                        });
                    }
                    return this.setState({
                        loading: false,
                        step: STEPS.CODE,
                        magicCode: "",
                    }, () => this.props.setLoginError(false, ""));
                });
        });

    }

    handleVerifyCode = (email, code) => {

        if (!code || code.length !== 6) {
            return this.props.setLoginError(true, "Please enter the 6-digit code from your email.");
        }

        return this.setState({ loading: true }, () => {
            return verifyMagicLink(email, code)
                .then(result => {
                    if (result.user) {
                        this.props.clearLogins();
                        this.props.setUserData(result.user);
                        this.props.setAuth(true);
                        if (this.state.isNewUser) {
                            return this.props.history.push("/setup");
                        }
                        return this.props.history.push("/");
                    }
                    const message = result.expired
                        ? "That code has expired. Please go back and request a new one."
                        : "That code is incorrect. Please try again.";
                    return this.setState({ loading: false }, () => {
                        this.props.setLoginError(true, message);
                    });
                });
        });

    }

    handleBack = () => {

        this.props.setLoginError(false, "");
        return this.setState({ step: STEPS.EMAIL, magicCode: "", fullName: "", isNewUser: false });

    }

    handleResendCode = (email) => {

        if (this.state.isNewUser) {
            return this.handleCreateAccount(email, this.state.fullName);
        }
        return this.handleContinueWithEmail(email);

    }

    handleKeyPress = (e, email) => {

        if (e.key !== "Enter") return;

        if (this.state.step === STEPS.EMAIL) return this.handleContinueWithEmail(email);
        if (this.state.step === STEPS.NAME) return this.handleCreateAccount(email, this.state.fullName);
        if (this.state.step === STEPS.CODE) return this.handleVerifyCode(email, this.state.magicCode);

    }

    renderHeader = () => {

        const { step, isNewUser } = this.state;

        let title = "Welcome to J4Crew";
        let subtitle = "Sign in or sign up with your email";
        let stepLabel = "Step 1 of 2";

        if (step === STEPS.NAME) {
            title = "Almost there";
            subtitle = "We just need your name";
            stepLabel = "Step 2 of 3";
        } else if (step === STEPS.CODE) {
            title = isNewUser ? "Check your email" : "Welcome back";
            subtitle = "Enter the 6-digit code we just sent you";
            stepLabel = isNewUser ? "Step 3 of 3" : "Step 2 of 2";
        }

        return (
            <div className="login-form-header-container" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="login-form-step-label">{stepLabel}</span>
                <span className="login-form-header-title">{title}</span>
                <span className="login-form-header-sidenote">{subtitle}</span>
            </div>
        );

    }

    renderError = () => {

        const { login } = this.props;

        if (!login.error || !login.errorMessage) return null;

        return (
            <div className="login-form-error-container">
                <p className="login-form-error-text">{login.errorMessage}</p>
            </div>
        );

    }

    renderEmailStep = () => {

        const { login } = this.props;
        const { loading } = this.state;

        return (
            <>
                <div className="margin-top-2x">
                    <TextInputWithIcon
                        value={login.email}
                        onChangeText={this.props.setLoginEmail}
                        iconName="mail"
                        placeholder={"Your Email"}
                        hide={false}
                        error={login.error}
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        autoFocus={true}
                        name="email"
                    />
                </div>

                {this.renderError()}

                <div className="margin-top-2x">
                    <Button onClick={() => this.handleContinueWithEmail(login.email)} loading={loading} title={"Continue"} type={"primary"} />
                </div>

                <div className="login-form-terms-text">
                    By continuing you agree to our <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </div>
            </>
        );

    }

    renderNameStep = () => {

        const { login } = this.props;
        const { loading, fullName } = this.state;

        return (
            <>
                <div className="login-form-info-banner">
                    Signing up as <strong>{login.email}</strong>
                </div>

                <div className="margin-top-2x">
                    <TextInputWithIcon
                        value={fullName}
                        onChangeText={(v) => this.setState({ fullName: v })}
                        iconName="user"
                        placeholder={"Full Name"}
                        hide={false}
                        error={login.error}
                        autoComplete="name"
                        autoFocus={true}
                        name="name"
                    />
                </div>

                {this.renderError()}

                <div className="margin-top-2x">
                    <Button onClick={() => this.handleCreateAccount(login.email, fullName)} loading={loading} title={"Send Me a Code"} type={"primary"} />
                </div>

                <div className="margin-top-2x" style={{ textAlign: "center" }}>
                    <a className="login-form-back-link" onClick={this.handleBack}>Use a different email</a>
                </div>
            </>
        );

    }

    renderCodeStep = () => {

        const { login } = this.props;
        const { loading, magicCode } = this.state;

        return (
            <>
                <div className="login-form-info-banner">
                    Code sent to <strong>{login.email}</strong>. It expires in 15 minutes.
                </div>

                <div className="margin-top-2x">
                    <TextInputWithIcon
                        value={magicCode}
                        onChangeText={(v) => this.setState({ magicCode: v.replace(/\D/g, "").slice(0, 6) })}
                        iconName="lock"
                        placeholder={"6-digit code"}
                        hide={false}
                        error={login.error}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus={true}
                        maxLength={6}
                        name="one-time-code"
                    />
                </div>

                {this.renderError()}

                <div className="margin-top-2x">
                    <Button onClick={() => this.handleVerifyCode(login.email, magicCode)} loading={loading} title={"Log In"} type={"primary"} />
                </div>

                <div className="margin-top-2x" style={{ display: "flex", justifyContent: "space-between" }}>
                    <a className="login-form-back-link" onClick={() => this.handleResendCode(login.email)}>Resend code</a>
                    <a className="login-form-back-link" onClick={this.handleBack}>Use a different email</a>
                </div>
            </>
        );

    }

    render() {

        const { step } = this.state;
        const { login } = this.props;

        return(
            <div className="login-form-container" tabIndex={0} onKeyPress={(e) => this.handleKeyPress(e, login.email)}>
                {this.renderHeader()}
                {step === STEPS.EMAIL && this.renderEmailStep()}
                {step === STEPS.NAME && this.renderNameStep()}
                {step === STEPS.CODE && this.renderCodeStep()}
            </div>
        );

    }

}

const mapStateToProps = (state) => {
    return {
        login: state.login,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        ...bindActionCreators({ setAuth, setUserData, setLoginEmail, setLoginError, clearLogins }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Login);
